export const demoDomain = "example.com";
export const demoCompany = "Example Ltd";

export const demoTrend = [
  { label: "Jan", vulnerabilities: 12, assets: 3, credentials: 6 },
  { label: "Feb", vulnerabilities: 10, assets: 4, credentials: 5 },
  { label: "Mar", vulnerabilities: 8, assets: 5, credentials: 4 },
  { label: "Apr", vulnerabilities: 6, assets: 5, credentials: 3 },
  { label: "May", vulnerabilities: 5, assets: 5, credentials: 2 },
];

export const demoFindings = [
  {
    id: "company-password-exposed",
    title: "Company password exposed",
    severity: "Critical",
    score: "9.8/10",
    numericScore: 9.8,
    category: "Credential Exposure",
    host: "example.com",
    affectedAsset: "example.com employee identity",
    status: "Fix now",
    shortDescription:
      "A company password was found in a public data breach and could be used to access business accounts.",
    narrative:
      "A credential associated with example.com has appeared in a known breach collection. Criminals commonly test exposed usernames and credentials against email, VPN, Microsoft 365, cloud platforms, and remote access portals. Even if the credential is old, reuse can still create a path to account takeover.",
    evidence: [
      "Breach source: public breach collection",
      "Account pattern: user@example.com",
      "Secret value: redacted",
      "Exposure type: credential exposure",
      "Confidence: high",
    ],
    downstreamImpacts: [
      "Mailbox compromise",
      "Business email compromise",
      "Cloud application takeover",
      "Invoice fraud",
      "Wider internal access if the credential is reused",
    ],
    remediation: [
      "Force a credential reset for the affected user.",
      "Confirm multi-factor authentication is enabled.",
      "Review recent login history.",
      "Block known compromised credentials.",
      "Check whether the same credential was reused on VPN, email, cloud, or admin portals.",
      "Monitor for new leaked credentials.",
    ],
    businessImpact:
      "Compromised accounts can lead to invoice fraud, mailbox compromise, customer data exposure, and wider system access.",
  },
  {
    id: "email-server-can-be-compromised",
    title: "Email server can be compromised",
    severity: "High",
    score: "9.3/10",
    numericScore: 9.3,
    category: "Email Security Misconfiguration",
    host: "mail.example.com",
    affectedAsset: "mail.example.com",
    status: "Fix now",
    shortDescription:
      "Weak protections could allow attackers to read, send, or intercept company email.",
    narrative:
      "Weak email security controls can expose an organisation to phishing, spoofing, interception, or mailbox abuse. Email infrastructure is one of the most targeted external services because it is directly connected to identity, finance, customers, suppliers, and internal operations.",
    evidence: [
      "Affected asset: mail.example.com",
      "Checks: SPF/DKIM/DMARC weakness",
      "Exposure type: mail security misconfiguration",
      "Confidence: high",
    ],
    downstreamImpacts: [
      "Phishing from trusted-looking domains",
      "Email interception risk",
      "Business email compromise",
      "Customer trust impact",
      "Increased likelihood of successful fraud attempts",
    ],
    remediation: [
      "Enforce SPF, DKIM, and DMARC.",
      "Set DMARC to quarantine or reject once tested.",
      "Disable insecure mail protocols where possible.",
      "Patch mail server software.",
      "Restrict administrative access.",
      "Monitor suspicious authentication attempts.",
    ],
    businessImpact:
      "Email compromise can result in fraud, phishing, data theft, reputational damage, and operational disruption.",
  },
  {
    id: "remote-access-open-to-the-internet",
    title: "Remote access open to the internet",
    severity: "Medium",
    score: "6.8/10",
    numericScore: 6.8,
    category: "Internet Exposure",
    host: "remote.example.com",
    affectedAsset: "remote.example.com",
    status: "Fix now",
    shortDescription:
      "An internet-facing remote login service may allow unauthorised access or ransomware.",
    narrative:
      "A remote access service is exposed directly to the internet. These portals are frequently targeted using automated login attempts, stolen credentials, and known software vulnerabilities. They are also common ransomware entry points.",
    evidence: [
      "Affected asset: remote.example.com",
      "Open service: remote login portal",
      "Exposure type: internet-facing remote access",
      "Confidence: high",
    ],
    downstreamImpacts: [
      "Unauthorised access",
      "Host compromise",
      "Ransomware deployment",
      "Privilege escalation",
      "Lateral movement into internal systems",
    ],
    remediation: [
      "Restrict remote access behind VPN or zero-trust access.",
      "Enforce MFA.",
      "Patch the remote access service.",
      "Disable unused accounts.",
      "Apply IP allowlisting where appropriate.",
      "Rate-limit failed login attempts.",
      "Monitor failed and suspicious logins.",
    ],
    businessImpact:
      "Successful compromise could allow attackers to access internal systems, deploy ransomware, or steal sensitive data.",
  },
  {
    id: "missing-dmarc-enforcement",
    title: "DMARC policy not enforced",
    severity: "Medium",
    score: "6.4/10",
    numericScore: 6.4,
    category: "Email Security",
    host: "example.com",
    affectedAsset: "example.com DNS",
    status: "Plan fix",
    shortDescription: "The domain is not configured to reject spoofed email.",
    narrative:
      "A mail policy is present but not enforced, allowing criminals to imitate the domain more easily during phishing campaigns.",
    evidence: ["DNS check: DMARC p=none", "Confidence: medium"],
    downstreamImpacts: ["Domain spoofing", "Supplier phishing", "Customer trust impact"],
    remediation: ["Move DMARC from monitoring to quarantine, then reject after validation."],
    businessImpact: "Criminals may impersonate the organisation in phishing campaigns.",
  },
  {
    id: "outdated-web-headers",
    title: "Website security headers need improvement",
    severity: "Medium",
    score: "5.9/10",
    numericScore: 5.9,
    category: "Web Application Hardening",
    host: "www.example.com",
    affectedAsset: "www.example.com",
    status: "Plan fix",
    shortDescription: "Several defensive HTTP headers are missing.",
    narrative:
      "Missing browser security headers can reduce protection against clickjacking, content injection, and downgrade-style risks.",
    evidence: ["Header check: missing Content-Security-Policy", "Confidence: medium"],
    downstreamImpacts: ["Browser-side attack exposure", "Reduced user protection"],
    remediation: ["Add CSP, HSTS, X-Frame-Options or frame-ancestors, and related headers."],
    businessImpact: "Users receive fewer browser protections when visiting the site.",
  },
  {
    id: "stale-subdomain-found",
    title: "Stale subdomain discovered",
    severity: "Low",
    score: "3.8/10",
    numericScore: 3.8,
    category: "Asset Hygiene",
    host: "old.example.com",
    affectedAsset: "old.example.com",
    status: "Review",
    shortDescription: "A legacy subdomain appears in public DNS.",
    narrative:
      "A stale public asset should be reviewed to confirm it is still required and correctly maintained.",
    evidence: ["DNS record: old.example.com", "Confidence: medium"],
    downstreamImpacts: [
      "Unmonitored asset exposure",
      "Potential takeover if pointed at abandoned services",
    ],
    remediation: [
      "Remove the record if unused or bring it under standard monitoring and patching.",
    ],
    businessImpact: "Forgotten public assets can become unmanaged attack paths.",
  },
];

export const demoAssets = [
  {
    id: "asset-root",
    ipAddress: "203.0.113.10",
    domainName: "example.com",
    operatingSystem: "Managed DNS",
    openPorts: [53, 443],
    vulnerabilities: ["DMARC policy not enforced"],
  },
  {
    id: "asset-web",
    ipAddress: "203.0.113.20",
    domainName: "www.example.com",
    operatingSystem: "Ubuntu Linux",
    openPorts: [80, 443],
    vulnerabilities: ["Website security headers need improvement"],
  },
  {
    id: "asset-mail",
    ipAddress: "203.0.113.30",
    domainName: "mail.example.com",
    operatingSystem: "Linux mail gateway",
    openPorts: [25, 587, 993],
    vulnerabilities: ["Email server can be compromised"],
  },
  {
    id: "asset-remote",
    ipAddress: "203.0.113.40",
    domainName: "remote.example.com",
    operatingSystem: "Windows Server",
    openPorts: [443],
    vulnerabilities: ["Remote access open to the internet"],
  },
  {
    id: "asset-old",
    ipAddress: "203.0.113.50",
    domainName: "old.example.com",
    operatingSystem: "Unknown",
    openPorts: [80],
    vulnerabilities: ["Stale subdomain discovered"],
  },
];

export const demoExposures = [
  "HTTPS website exposed on TCP/443",
  "Mail service exposed on TCP/25 and TCP/587",
  "Remote access portal exposed on TCP/443",
  "DNS records indicate an external email dependency",
];

export const demoBreaches = [
  "Public breach collection: user@example.com",
  "Credential reuse risk: redacted secret",
  "Mailbox takeover risk: MFA recommended",
];

export const demoCredentials = [
  {
    id: "credential-user",
    username: "user@example.com",
    asset: "example.com employee identity",
    breachSource: "Public breach collection",
    discoveredAt: "2026-05-27",
  },
  {
    id: "credential-mail",
    username: "accounts@example.com",
    asset: "mail.example.com",
    breachSource: "Reused password corpus",
    discoveredAt: "2026-05-24",
  },
];

export const demoReports = [
  { id: "report-exec", title: "Executive exposure summary", status: "Ready", date: "Today" },
  { id: "report-it", title: "Provider remediation checklist", status: "Ready", date: "Today" },
];
